import { ProposalFormData } from '../types';
import {
  BuildupStep,
  buildProjectLevelSplit,
  nreBuildupSteps,
  NreMillDetail,
  perPipeBuildupSteps,
  PerPipeDetail,
  ProjectLevelSplit,
  scanningBuildupSteps,
  ScanningDetail,
  gainShareBuildupSteps,
  GainShareDetail,
  perUnitLineBuildupSteps,
  PerUnitDetail,
  perInspectionBuildupSteps,
  PerInspectionDetail,
} from './internalSplitBuildup';
import { buildCostLines, CostLine } from './internalCostBreakdown';

type LineDetail = Record<string, unknown>;

// Backend separator between a per-mill line label and the mill name, e.g.
// "Per-pipe data charge -- Mill A" or "MT tracking charge -- Mill A".
const MILL_LABEL_SEPARATOR = ' -- ';
const ONBOARDING_NRE_LABEL = 'Onboarding (NRE)';

export interface PricingSheetProductRow {
  productName: string;
  configuration: string[];  // each entry renders as its own line
  amount: number;
}

export interface PricingSheetSummaryRow {
  label: string;
  amount: number;
}

// Plain-English complexity for a source data format, used in the customer-facing
// "Basis of this quote" block. Unknown or absent formats yield no complexity.
const FORMAT_COMPLEXITY: Record<string, string> = {
  api: 'Low',
  nativeDigital: 'Low',
  spreadsheet: 'Standard',
  scannedPdf: 'Higher',
  handwritten: 'Highest',
};

function complexityForFormat(format?: string): string | undefined {
  return format ? FORMAT_COMPLEXITY[format] : undefined;
}

// One scope line in the "Basis of this quote" block: a module's report count,
// data-source complexity, and volume, in plain English. Only fields the
// breakdown actually carries are populated; the PDF omits absent values.
export interface ScopeSummaryLine {
  productName: string;
  reportCount?: number;
  reportUnit?: string;  // "per pipe" / "per inspection" / "per joint"
  complexity?: string;  // Low / Standard / Higher / Highest
  volume?: number;
  volumeUnit?: string;  // "pipes" / "inspections" / "joints"
  // Pre-composed text for modules that do not fit the report/complexity/volume
  // shape (e.g. Welding, which is driven by system count and mode). When set,
  // the PDF prints it verbatim.
  description?: string;
}

// The basis-of-quote scope: per-module report/complexity/volume lines plus the
// project duration and platform user count that drive the price.
export interface ScopeSummary {
  lines: ScopeSummaryLine[];
  durationMonths: number;
  numberOfUsers: number;
}

export interface PricingSheetData {
  referenceNumber: string;
  date: string;
  customerName: string;
  customerAttn: string;
  customerEmail: string;
  customerPhone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  projectTitle: string;
  startDate: string;
  endDate: string;
  durationLabel: string;
  paymentTerms: string;
  validityPeriod: string;
  products: PricingSheetProductRow[];
  summaryRows: PricingSheetSummaryRow[];
  grandTotal: number;
  // Module ids present in this proposal (priceBreakdown.products keys with a
  // non-zero total), used to gate the per-module assumption blocks in the PDF.
  moduleIds: string[];
  // The figures the customer sees as the basis of the quote: per-module scope
  // plus project duration and platform user count.
  scope: ScopeSummary;
}

export interface WeldingMachineRow {
  systemName: string;
  actualUnits: number;
  spareUnits: number;
  sparePricingMode: string;
  swChargeableUnits: number;
  mode: string;
  hwCost: number;
  swCost: number;
  rowTotal: number;
}

export interface ModuleSplitOneTimeComponent {
  label: string;
  amount: number;
  detail?: LineDetail;
}

// One priced line under a mill: its onboarding (NRE) or its tracking charge.
export interface ModuleSplitLine {
  label: string;
  amount: number;
  detail?: LineDetail;
  // The itemised cost-to-price build-up: the onboarding math on an NRE line, or
  // the cost-per-pipe math on a per-pipe tracking line. Rendered as a table.
  buildupSteps?: BuildupStep[];
}

// A mill (source) and the lines charged for it: onboarding plus tracking.
export interface ModuleSplitMillGroup {
  name: string;
  lines: ModuleSplitLine[];
}

export interface ModuleSplitRow {
  productName: string;
  // Per-mill grouping: each mill's onboarding (NRE) and tracking charge together.
  mills: ModuleSplitMillGroup[];
  // One-time lines not tied to a single mill (add-ons, scanning, module floor).
  other: ModuleSplitLine[];
  oneTime: {
    components: ModuleSplitOneTimeComponent[];
    subtotal: number;
  };
  total: number;
}

export interface ModuleBreakdownDetails {
  // Pre-margin internal cost (COGS) for the module, rolled up by the backend.
  cost?: number;
  oneTime?: {
    components?: Array<{ label?: string; amount?: number; detail?: LineDetail }>;
    subtotal?: number;
  };
  recurring?: {
    components?: Array<{
      label?: string;
      monthly?: number;
      contractTotal?: number;
      detail?: LineDetail;
    }>;
    monthlySubtotal?: number;
    months?: number;
    contractTotal?: number;
  };
  total?: number;
  // Welding-only: total system count and the live/offline mode mix, used to
  // build the welding scope line in the customer quote basis.
  totalQuantity?: number;
  modeMix?: { liveUnits?: number; offlineOnlyUnits?: number };
}

// One row of the internal cost summary: a module's pre-margin cost against the
// quoted price, with the implied margin and the cost lines that sum to it.
export interface ModuleCostRow {
  label: string;
  cost: number;
  price: number;
  marginPct: number;
  lines: CostLine[];
}

export interface InternalSplitData extends PricingSheetData {
  durationMonths: number;
  weldingMachines: WeldingMachineRow[];
  moduleSplits: ModuleSplitRow[];
  // Internal cost-vs-price summary, one row per priced module.
  costRows: ModuleCostRow[];
  totalModuleCost: number;
  totalModulePrice: number;
  hardwareCost: number;
  softwareCost: number;
  discountPercentage: number;
  mobilizationCost: number;
  basePrice: number;
  otherCosts: {
    internet: number;
    cloud: number;
    cloudCompute: number;
    cloudStorage: number;
    remoteSupport: number;
    training: number;
    provisioning: number;
    onFieldSupport: number;
  };
  totalOtherCosts: number;
  weldingTotal: number;
  projectLevel: ProjectLevelSplit | null;
}

const WELDING_HW_IDS = ['m500', 'p600z', 'p625', 'iwm', 'manual'];

// Label for the project-level platform + retention rollup in the Combined Price Summary.
const PLATFORM_SUBSCRIPTION_LABEL = 'Platform & Subscription';

// Stable render order for the internal split's per-module sections.
const MODULE_SPLIT_PRODUCT_IDS = ['materialTraceability', 'pipeFitter', 'ndt', 'coating'];

// Canonical display order for module sections: Material Traceability, Welding,
// NDT, Coating, then anything else (PipeFitter). Used to order the internal cost
// summary so modules always read in the same flow regardless of stored key order.
const MODULE_DISPLAY_ORDER = ['materialTraceability', 'welding', 'ndt', 'coating', 'pipeFitter'];

function orderModuleIds(ids: string[]): string[] {
  const known = MODULE_DISPLAY_ORDER.filter(id => ids.includes(id));
  const rest = ids.filter(id => !MODULE_DISPLAY_ORDER.includes(id));
  return [...known, ...rest];
}

export const PRODUCT_DISPLAY_NAMES: Record<string, string> = {
  materialTraceability: 'Material Traceability',
  pipeFitter: 'PipeFitter',
  welding: 'Welding',
  ndt: 'NDT',
  coating: 'Coating',
};

const WELDING_HARDWARE_NAMES: Record<string, string> = {
  m500: 'M-500',
  p600z: 'P-600z',
  p625: 'P-625',
  iwm: 'IWM',
  manual: 'Manual',
};

const SPARE_MODE_LABELS: Record<string, string> = {
  hw_only: 'HW only',
  hw_sw: 'HW+SW',
  hw_sw_other: 'HW+SW+Infra',
};

const PRODUCT_ITEM_NAMES: Record<string, Record<string, string>> = {
  materialTraceability: {
    pipe: 'Pipes',
    gas: 'Gas',
    wire: 'Wire',
    coating: 'Coating materials',
  },
  pipeFitter: {
    isometric: 'Isometric Drawing',
    spoolTracking: 'Spool Tracking',
    fitUp: 'Fit-Up Management',
  },
  ndt: {
    rtFilm: 'RT Film Tracking',
    ndtReport: 'NDT Report Management',
    defectTracking: 'Defect Tracking',
  },
  coating: {
    surfacePrep: 'Surface Preparation',
    coatingApplication: 'Coating Application',
    coatingInspection: 'Coating Inspection',
    repairTracking: 'Repair Tracking',
  },
};

function buildSimpleConfigLines(
  productId: string,
  items: Record<string, { selected: boolean }>
): string[] {
  const nameMap = PRODUCT_ITEM_NAMES[productId] ?? {};
  const selected = Object.entries(items)
    .filter(([, item]) => item.selected)
    .map(([id]) => nameMap[id] ?? id);
  return selected.length > 0 ? selected : ['Selected'];
}

function formatDisplayDate(iso: string): string {
  if (!iso) return '';
  // Append T00:00:00 (no Z) so the ISO date-only string is parsed as local midnight,
  // not UTC midnight — prevents off-by-one shifts in non-UTC timezones.
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const AVG_DAYS_PER_MONTH = 30.44;

// Project duration in whole months, from the explicit duration field (converted
// from years/days when needed) or the start/end dates. Zero when neither is set.
function durationMonthsFromForm(formData: ProposalFormData): number {
  const value = Number(formData.duration?.value);
  if (Number.isFinite(value) && value > 0) {
    const unit = formData.duration?.unit;
    if (unit === 'years') return Math.round(value * 12);
    if (unit === 'days') return Math.round(value / AVG_DAYS_PER_MONTH);
    return Math.round(value);
  }
  if (formData.startDate && formData.endDate) {
    const ms =
      new Date(`${formData.endDate}T00:00:00`).getTime() -
      new Date(`${formData.startDate}T00:00:00`).getTime();
    if (ms > 0) return Math.round(ms / (1000 * 60 * 60 * 24 * AVG_DAYS_PER_MONTH));
  }
  return 0;
}

// A module's tracking line and the report/volume unit nouns it implies, by the
// per-record cost-model key its detail carries.
const TRACKING_UNITS: Array<{ key: string; report: string; volume: string }> = [
  { key: 'costPerPipe', report: 'per pipe', volume: 'pipes' },
  { key: 'costPerInspection', report: 'per inspection', volume: 'inspections' },
  { key: 'costPerJoint', report: 'per joint', volume: 'joints' },
];

interface ScopeSourceDetail {
  format?: string;
  reportsPerPipe?: number;
  reportsPerInspection?: number;
  reportsPerJoint?: number;
  volume?: number;
}

// Extract one scope line for a module from its breakdown one-time components:
// the report count and unit from the first tracking line, the total volume
// across its tracking lines, and the complexity from the first source format
// (NRE detail rows, else the tracking line detail). Returns null when the
// module carries no scope detail to surface.
function scopeLineForModule(
  productId: string,
  details?: ModuleBreakdownDetails | null
): ScopeSummaryLine | null {
  const components = details?.oneTime?.components ?? [];
  if (components.length === 0) return null;

  let reportCount: number | undefined;
  let reportUnit: string | undefined;
  let volume = 0;
  let hasVolume = false;
  let complexity: string | undefined;

  // Complexity from the first onboarding source format, when present.
  const nre = components.find(c => c.label === ONBOARDING_NRE_LABEL);
  if (nre?.detail) {
    const rows =
      (nre.detail.mills as Array<{ format?: string }> | undefined) ??
      (nre.detail.vendors as Array<{ format?: string }> | undefined) ??
      (nre.detail.contractors as Array<{ format?: string }> | undefined);
    const first = Array.isArray(rows) ? rows.find(r => r?.format) : undefined;
    complexity = complexityForFormat(first?.format);
  }

  components.forEach(c => {
    const detail = c.detail as ScopeSourceDetail | undefined;
    if (!detail) return;
    const unit = TRACKING_UNITS.find(u => u.key in (detail as Record<string, unknown>));
    if (!unit) return;
    if (typeof detail.volume === 'number') {
      volume += detail.volume;
      hasVolume = true;
    }
    if (reportCount === undefined) {
      const reports =
        detail.reportsPerPipe ?? detail.reportsPerInspection ?? detail.reportsPerJoint;
      if (typeof reports === 'number' && reports > 0) {
        reportCount = reports;
        reportUnit = unit.report;
      }
    }
    if (complexity === undefined) complexity = complexityForFormat(detail.format);
    if (reportUnit === undefined) reportUnit = unit.report;
  });

  const volumeUnit = reportUnit
    ? TRACKING_UNITS.find(u => u.report === reportUnit)?.volume
    : undefined;

  const line: ScopeSummaryLine = { productName: PRODUCT_DISPLAY_NAMES[productId] ?? productId };
  if (reportCount !== undefined) line.reportCount = reportCount;
  if (reportUnit !== undefined) line.reportUnit = reportUnit;
  if (complexity !== undefined) line.complexity = complexity;
  if (hasVolume) {
    line.volume = volume;
    line.volumeUnit = volumeUnit;
  }

  // Nothing surfaceable: no report count, complexity, or volume.
  if (line.reportCount === undefined && line.complexity === undefined && line.volume === undefined) {
    return null;
  }
  return line;
}

// Build the customer-facing basis-of-quote scope: one line per priced module
// that carries scope detail, plus the project duration and platform user count.
// Welding does not carry a report/complexity/volume cost-model line; its scope
// is the number of welding systems and the live/offline operating mode. Returns
// null when no welding systems are priced.
function weldingScopeLine(
  details?: ModuleBreakdownDetails | null
): ScopeSummaryLine | null {
  const units = Math.round(details?.totalQuantity ?? 0);
  if (units <= 0) return null;

  const live = details?.modeMix?.liveUnits ?? 0;
  const offline = details?.modeMix?.offlineOnlyUnits ?? 0;
  let mode = '';
  if (live > 0 && offline > 0) mode = 'live and offline modes';
  else if (live > 0) mode = 'live mode';
  else if (offline > 0) mode = 'offline mode';

  const noun = units === 1 ? 'welding system' : 'welding systems';
  const description = mode
    ? `${units.toLocaleString('en-US')} ${noun}, ${mode}`
    : `${units.toLocaleString('en-US')} ${noun}`;
  return { productName: PRODUCT_DISPLAY_NAMES.welding ?? 'Welding', description };
}

export function buildScopeSummary(
  formData: ProposalFormData,
  products?: Record<string, { total?: number; details?: ModuleBreakdownDetails }>
): ScopeSummary {
  const priced = Object.entries(products ?? {}).filter(([, p]) => (p.total ?? 0) > 0);
  const byId = new Map(priced);
  const lines: ScopeSummaryLine[] = [];
  orderModuleIds(priced.map(([id]) => id)).forEach(productId => {
    const product = byId.get(productId);
    if (!product) return;
    const line =
      productId === 'welding'
        ? weldingScopeLine(product.details)
        : scopeLineForModule(productId, product.details);
    if (line) lines.push(line);
  });
  return {
    lines,
    durationMonths: durationMonthsFromForm(formData),
    numberOfUsers: formData.numberOfUsers ?? 0,
  };
}

export function buildPricingSheetData(
  formData: ProposalFormData,
  priceBreakdown: {
    products?: Record<string, { total?: number; details?: ModuleBreakdownDetails }>;
    projectLevel?: { total?: number } | null;
    total?: number;
  },
  totalPrice: number,
  refNumber?: string
): PricingSheetData {
  const now = new Date();
  const dateStamp = now.toISOString().slice(0, 10).replace(/-/g, '');
  const seq = String(
    Math.floor((now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()) % 1000)
  ).padStart(3, '0');
  const referenceNumber = refNumber?.trim() || `PR-${dateStamp}-${seq}`;

  const date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  let durationLabel = '';
  if (formData.duration?.value) {
    durationLabel = `${formData.duration.value} ${formData.duration.unit}`;
  } else if (formData.startDate && formData.endDate) {
    const ms = new Date(`${formData.endDate}T00:00:00`).getTime() - new Date(`${formData.startDate}T00:00:00`).getTime();
    const months = Math.round(ms / (1000 * 60 * 60 * 24 * 30.44));
    durationLabel = `${months} months`;
  }

  const validityPeriod = formData.validityPeriod ? `${formData.validityPeriod} days` : '';

  const moduleIds: string[] = Object.entries(priceBreakdown.products ?? {})
    .filter(([, product]) => (product.total ?? 0) > 0)
    .map(([productId]) => productId);

  const products: PricingSheetProductRow[] = Object.entries(priceBreakdown.products ?? {})
    .filter(([, product]) => (product.total ?? 0) > 0)
    .map(([productId, product]) => {
      const productName = PRODUCT_DISPLAY_NAMES[productId] ?? productId;
      const productItems = formData.selectedProducts[productId]?.items ?? {};
      const hasItemData = Object.values(productItems).some(i => i.selected);
      // Welding is shown as a single "Included" line: the hardware quantities
      // and modes are commercial detail the customer sheet does not itemise.
      const configuration: string[] =
        productId === 'welding'
          ? ['Included']
          : hasItemData
            ? buildSimpleConfigLines(productId, productItems as Record<string, { selected: boolean }>)
            : ['Included'];
      return { productName, configuration, amount: product.total ?? 0 };
    });

  // Combined Price Summary: one row per selected product (using its breakdown total),
  // then a single project-level platform + retention rollup when present and non-zero.
  const summaryRows: PricingSheetSummaryRow[] = Object.entries(priceBreakdown.products ?? {})
    .filter(([, product]) => (product.total ?? 0) > 0)
    .map(([productId, product]) => ({
      label: PRODUCT_DISPLAY_NAMES[productId] ?? productId,
      amount: product.total ?? 0,
    }));

  // The project-level platform + retention is not a per-module product, so it is
  // added as its own price-sheet line (and summary row) with the recurring
  // component labels as its sub-lines.
  const projectLevel = priceBreakdown.projectLevel as
    | { total?: number; recurring?: { components?: Array<{ label?: string }> } }
    | null
    | undefined;
  const platformTotal = projectLevel?.total ?? 0;
  if (platformTotal > 0) {
    const componentLines = (projectLevel?.recurring?.components ?? [])
      .map(c => c.label ?? '')
      .filter(Boolean);
    products.push({
      productName: PLATFORM_SUBSCRIPTION_LABEL,
      configuration: componentLines.length > 0 ? componentLines : ['Platform subscription'],
      amount: platformTotal,
    });
    summaryRows.push({ label: PLATFORM_SUBSCRIPTION_LABEL, amount: platformTotal });
  }

  return {
    referenceNumber,
    date,
    customerName: formData.customerName,
    customerAttn: formData.customerAttn,
    customerEmail: formData.customerEmail,
    customerPhone: formData.customerPhone,
    addressLine1: formData.customerAddressLine1,
    addressLine2: formData.customerAddressLine2,
    city: formData.customerCity,
    state: formData.customerState,
    postalCode: formData.customerPostalCode,
    country: formData.customerCountry,
    projectTitle: formData.projectTitle,
    startDate: formatDisplayDate(formData.startDate),
    endDate: formatDisplayDate(formData.endDate),
    durationLabel,
    paymentTerms: formData.paymentTerms,
    validityPeriod,
    products,
    summaryRows,
    grandTotal: totalPrice,
    moduleIds,
    scope: buildScopeSummary(formData, priceBreakdown.products),
  };
}

const num = (v: unknown): number => (typeof v === 'number' ? v : 0);

const currencyRate = (n: number): string =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// The mill name a per-mill line is tied to, parsed from its " -- <name>" suffix.
function millNameFromLabel(label: string): string | null {
  const idx = label.indexOf(MILL_LABEL_SEPARATOR);
  return idx === -1 ? null : label.slice(idx + MILL_LABEL_SEPARATOR.length).trim();
}

// Relabel a per-mill tracking charge so it reads as the explicit math, e.g.
// "MT tracking charge -- Mill A -- 120 pipes x $5.43/pipe". The rate shown is
// the net charged rate (line amount / pipes), so it reconciles with the line
// total after any duration discount. Falls back to the original label when the
// detail does not carry pipe count.
function trackingChargeLabel(
  label: string, millName: string, amount: number, detail?: LineDetail
): string {
  // NDT per-vendor lines read "Per-inspection charge -- Vendor -- N insp x $/insp".
  if (detail && 'costPerInspection' in detail) {
    const insp = num(detail?.volume);
    const rate = insp > 0 ? amount / insp : num(detail?.pricePerInspection);
    const base = `Per-inspection charge${MILL_LABEL_SEPARATOR}${millName}`;
    if (insp > 0 && rate > 0) {
      return `${base}${MILL_LABEL_SEPARATOR}${insp.toLocaleString('en-US')} insp x ${currencyRate(rate)}/insp`;
    }
    return base;
  }
  // Coating per-contractor lines read "Per-joint coating record -- Contractor -- N joints x $/joint".
  if (detail && 'costPerJoint' in detail) {
    const joints = num(detail?.volume);
    const rate = joints > 0 ? amount / joints : num(detail?.pricePerJoint);
    const base = `Per-joint coating record${MILL_LABEL_SEPARATOR}${millName}`;
    if (joints > 0 && rate > 0) {
      return `${base}${MILL_LABEL_SEPARATOR}${joints.toLocaleString('en-US')} joints x ${currencyRate(rate)}/joint`;
    }
    return base;
  }
  // Material Traceability per-mill tracking charge, with the net per-pipe rate.
  if (detail && 'costPerPipe' in detail) {
    const pipes = num(detail?.volume);
    const rate = pipes > 0 ? amount / pipes : num(detail?.pricePerPipe);
    const base = `MT tracking charge${MILL_LABEL_SEPARATOR}${millName}`;
    if (pipes > 0 && rate > 0) {
      return `${base}${MILL_LABEL_SEPARATOR}${pipes} pipes x ${currencyRate(rate)}/pipe`;
    }
    return base;
  }
  // Any other per-source line (for example coating bolt-on hardware) keeps its
  // own label: the prefix before the source separator, plus the source name.
  const prefix = label.split(MILL_LABEL_SEPARATOR)[0].trim();
  return millName ? `${prefix}${MILL_LABEL_SEPARATOR}${millName}` : prefix;
}

// Group the one-time components by mill: each mill's onboarding (NRE) line and
// its tracking charge line together, with any non-mill line (add-ons, scanning,
// module floor) collected in `other`. The onboarding line carries a readable
// build-up formula drawn from the enriched per-source detail.
function buildMillGroups(
  components: Array<{ label?: string; amount?: number; detail?: LineDetail }>
): { mills: ModuleSplitMillGroup[]; other: ModuleSplitLine[] } {
  // Groups are keyed by a stable source id so two mills that share a name (or
  // both left blank, the default) stay distinct. The display header still uses
  // the mill name, falling back to a positional label when the name is blank.
  const groups = new Map<string, ModuleSplitMillGroup>();
  const order: string[] = [];
  const other: ModuleSplitLine[] = [];

  const ensure = (key: string, name: string): ModuleSplitMillGroup => {
    let group = groups.get(key);
    if (!group) {
      group = { name: name || `Source ${order.length + 1}`, lines: [] };
      groups.set(key, group);
      order.push(key);
    }
    return group;
  };

  components.forEach(c => {
    const label = c.label ?? '';
    const detail = c.detail;

    if (label === ONBOARDING_NRE_LABEL) {
      // The onboarding rows are keyed 'mills' (Material), 'vendors' (NDT), or
      // 'contractors' depending on the module; each carries the same NRE detail.
      const rows =
        (detail?.mills as NreMillDetail[] | undefined) ??
        (detail?.vendors as NreMillDetail[] | undefined) ??
        (detail?.contractors as NreMillDetail[] | undefined);
      const mills = Array.isArray(rows) ? rows : [];
      // One Onboarding (NRE) line per source, carrying its own build-up.
      mills.forEach((mill, i) => {
        const name = mill.name ?? '';
        const key = mill.id || name || `nre-${i}`;
        ensure(key, name).lines.unshift({
          label: ONBOARDING_NRE_LABEL,
          amount: num(mill.price),
          detail: mill as LineDetail,
          buildupSteps: nreBuildupSteps(mill),
        });
      });
      return;
    }

    const millName = millNameFromLabel(label);
    if (millName !== null) {
      const sourceId = (detail?.sourceId as string | undefined) || '';
      const groupKey = sourceId || millName || label;
      // The per-pipe (tracking) line carries an itemised cost build-up when its
      // detail holds the per-pipe components, so a reader can reconstruct the
      // cost-per-pipe, the margin, and any duration discount down to the net
      // charged rate. The line amount drives the discount reconciliation.
      const amount = c.amount ?? 0;
      const buildupSteps =
        detail && 'costPerPipe' in detail
          ? perPipeBuildupSteps(detail as PerPipeDetail, amount)
          : detail && ('costPerInspection' in detail || 'costPerJoint' in detail)
            ? perInspectionBuildupSteps(detail as PerInspectionDetail, amount)
            : undefined;
      ensure(groupKey, millName).lines.push({
        label: trackingChargeLabel(label, millName, amount, detail),
        amount,
        detail,
        ...(buildupSteps ? { buildupSteps } : {}),
      });
      return;
    }

    // A non-mill line (add-ons, scanning, reporting, algorithm, floor). The CRC
    // scanning service and the algorithm gain-share lines carry an itemised
    // build-up so a reader can reconstruct the total.
    const otherMode = detail ? (detail as { mode?: string }).mode : undefined;
    const isNreDetail = detail !== undefined && 'setupHours' in detail && 'totalHours' in detail;
    const isPerInspection =
      detail !== undefined && ('costPerInspection' in detail || 'costPerJoint' in detail);
    const otherSteps =
      otherMode === 'service'
        ? scanningBuildupSteps(detail as ScanningDetail)
        : otherMode === 'gainShare'
          ? gainShareBuildupSteps(detail as GainShareDetail, c.amount ?? 0)
          : otherMode === 'perJoint' || otherMode === 'flat' || otherMode === 'free'
            ? perUnitLineBuildupSteps(detail as PerUnitDetail, c.amount ?? 0)
            : isPerInspection
              ? perInspectionBuildupSteps(detail as PerInspectionDetail, c.amount ?? 0)
              : isNreDetail
                ? nreBuildupSteps(detail as NreMillDetail)
                : undefined;
    other.push({
      label,
      amount: c.amount ?? 0,
      detail,
      ...(otherSteps ? { buildupSteps: otherSteps } : {}),
    });
  });

  return { mills: order.map(key => groups.get(key)!), other };
}

// The calculate-price response entry for a single module: its rolled-up total
// and the one-time / recurring detail the split renders.
export interface ModuleBreakdown {
  total?: number;
  details?: ModuleBreakdownDetails;
}

// Build the per-mill split row for a single module's breakdown. Returns null
// when the breakdown carries no one-time or recurring detail to render.
export function buildModuleSplitRow(
  productId: string,
  breakdown?: ModuleBreakdown | null
): ModuleSplitRow | null {
  const details = breakdown?.details;
  if (!details || (!details.oneTime && !details.recurring)) return null;
  return toModuleSplitRow(productId, details);
}

function toModuleSplitRow(productId: string, details: ModuleBreakdownDetails): ModuleSplitRow {
  const oneTime = details.oneTime ?? {};
  const components = oneTime.components ?? [];
  const { mills, other } = buildMillGroups(components);
  return {
    productName: PRODUCT_DISPLAY_NAMES[productId] ?? productId,
    mills,
    other,
    oneTime: {
      components: components.map(c => ({
        label: c.label ?? '',
        amount: c.amount ?? 0,
        detail: c.detail,
      })),
      subtotal: oneTime.subtotal ?? 0,
    },
    total: details.total ?? 0,
  };
}

// Drives the internal split PDF's per-module sections off the breakdown the
// calculators already populate; the customer sheet stays blended. Recurring
// belongs only in the project-level platform section, not per module.
export function buildModuleSplitRows(
  products?: Record<string, { total?: number; details?: ModuleBreakdownDetails }>
): ModuleSplitRow[] {
  if (!products) return [];
  return MODULE_SPLIT_PRODUCT_IDS.reduce<ModuleSplitRow[]>((rows, productId) => {
    const product = products[productId];
    const details = product?.details;
    if (details && (details.oneTime || details.recurring)) {
      rows.push(toModuleSplitRow(productId, details));
    }
    return rows;
  }, []);
}

// Build the internal cost-vs-price summary: one row per priced module (in the
// same order as the product table), each carrying its pre-margin cost, quoted
// price, and implied margin. The project-level platform line is excluded; it is
// shown above in the price summary and is not a per-module cost.
export function buildModuleCostRows(
  products?: Record<string, { total?: number; details?: { cost?: number } }>
): ModuleCostRow[] {
  if (!products) return [];
  return orderModuleIds(Object.keys(products))
    .map((productId) => [productId, products[productId]] as const)
    .filter(([, product]) => (product.total ?? 0) > 0)
    .map(([productId, product]) => {
      const price = product.total ?? 0;
      const cost = product.details?.cost ?? 0;
      const marginPct = price > 0 ? ((price - cost) / price) * 100 : 0;
      return {
        label: PRODUCT_DISPLAY_NAMES[productId] ?? productId,
        cost,
        price,
        marginPct,
        lines: buildCostLines(productId, product.details as Record<string, unknown> | undefined),
      };
    });
}

export function buildInternalSplitData(
  formData: ProposalFormData,
  priceBreakdown: {
    products?: Record<string, {
      total?: number;
      details?: {
        cost?: number;
        durationMonths?: number;
        totalQuantity?: number;
        softwareChargeableUnits?: number;
        hardwareCost?: number;
        softwareCost?: number;
        mobilizationCost?: number;
        basePrice?: number;
        discountPercentage?: number;
        spares?: Record<string, {
          actualUnits?: number;
          spareUnits?: number;
          sparePricingMode?: 'hw_only' | 'hw_sw' | 'hw_sw_other';
          softwareChargeableUnits?: number;
        }>;
        otherCosts?: {
          internet?: number;
          cloud?: number;
          cloudCompute?: number;
          cloudStorage?: number;
          remoteSupport?: number;
          training?: number;
          provisioning?: number;
          onFieldSupport?: number;
        };
        totalOtherCosts?: number;
        totalPrice?: number;
        oneTime?: ModuleBreakdownDetails['oneTime'];
        recurring?: ModuleBreakdownDetails['recurring'];
      };
    }>;
    projectLevel?: Parameters<typeof buildProjectLevelSplit>[0];
    total?: number;
  },
  totalPrice: number,
  refNumber?: string
): InternalSplitData {
  const baseData = buildPricingSheetData(formData, priceBreakdown, totalPrice, refNumber);
  const moduleSplits = buildModuleSplitRows(priceBreakdown.products);
  const costRows = buildModuleCostRows(priceBreakdown.products);
  const totalModuleCost = costRows.reduce((s, r) => s + r.cost, 0);
  const totalModulePrice = costRows.reduce((s, r) => s + r.price, 0);
  const projectLevel = buildProjectLevelSplit(priceBreakdown.projectLevel);

  const wd = priceBreakdown.products?.welding?.details ?? {};
  const totalQty = wd.totalQuantity ?? 0;
  const totalSwChargeable = wd.softwareChargeableUnits ?? 0;

  const hwCostPerUnit = totalQty > 0 ? (wd.hardwareCost ?? 0) / totalQty : 0;
  const swCostPerUnit = totalSwChargeable > 0 ? (wd.softwareCost ?? 0) / totalSwChargeable : 0;

  const weldingItems = formData.selectedProducts['welding']?.items ?? {};

  const weldingMachines: WeldingMachineRow[] = WELDING_HW_IDS
    .filter(id => weldingItems[id]?.selected)
    .map(id => {
      const item = weldingItems[id];
      const spare = wd.spares?.[id] ?? {};

      const actualUnits = item.quantity ?? 0;
      const spareUnits = item.spareQuantity ?? 0;
      const totalUnits = actualUnits + spareUnits;
      const swChargeable = spare.softwareChargeableUnits ?? actualUnits;

      const isLive = weldingItems[`${id}_live`]?.selected ?? false;
      const isOffline = weldingItems[`${id}_offline`]?.selected ?? false;
      let mode = '';
      if (isLive) mode = 'Live';
      else if (isOffline) mode = 'Offline';

      const hwCost = hwCostPerUnit * totalUnits;
      const swCost = swCostPerUnit * swChargeable;

      return {
        systemName: WELDING_HARDWARE_NAMES[id] ?? id,
        actualUnits,
        spareUnits,
        sparePricingMode: SPARE_MODE_LABELS[item.sparePricingMode ?? 'hw_only'] ?? 'HW only',
        swChargeableUnits: swChargeable,
        mode,
        hwCost,
        swCost,
        rowTotal: hwCost + swCost,
      };
    });

  return {
    ...baseData,
    durationMonths: wd.durationMonths ?? 0,
    weldingMachines,
    moduleSplits,
    costRows,
    totalModuleCost,
    totalModulePrice,
    hardwareCost: wd.hardwareCost ?? 0,
    softwareCost: wd.softwareCost ?? 0,
    discountPercentage: wd.discountPercentage ?? 0,
    mobilizationCost: wd.mobilizationCost ?? 0,
    basePrice: wd.basePrice ?? 0,
    otherCosts: {
      internet: wd.otherCosts?.internet ?? 0,
      cloud: wd.otherCosts?.cloud ?? 0,
      cloudCompute: wd.otherCosts?.cloudCompute ?? 0,
      cloudStorage: wd.otherCosts?.cloudStorage ?? 0,
      remoteSupport: wd.otherCosts?.remoteSupport ?? 0,
      training: wd.otherCosts?.training ?? 0,
      provisioning: wd.otherCosts?.provisioning ?? 0,
      onFieldSupport: wd.otherCosts?.onFieldSupport ?? 0,
    },
    totalOtherCosts: wd.totalOtherCosts ?? 0,
    weldingTotal: priceBreakdown.products?.welding?.total ?? 0,
    projectLevel,
  };
}
